// RFC 2606 reserves `.invalid`, so a candidate that somehow kept this origin
// still names nothing reachable.
const GUARD_ORIGIN = "http://guard.invalid";

/**
 * Gates a redirect target down to a same-origin in-app path: returns the
 * re-serialised `pathname + search + hash`, or null when the candidate escapes
 * the origin — callers fall back to their own default on null.
 *
 * Resolving beats pattern-matching because the WHATWG parser is what an
 * attacker is really addressing: it reads "\" as "/" under a special scheme and
 * strips ASCII tab/LF/CR from anywhere in the input before parsing, so
 * "/\evil.test" and "/<TAB>/evil.test" both become "//evil.test" and resolve to
 * a foreign origin. One origin comparison catches every costume a blocklist
 * would have to enumerate. The result is re-serialised rather than echoed back,
 * so what the caller navigates to is what was measured.
 *
 * Two checks flank the comparison. A leading "//" is refused up front: it is
 * protocol-relative, and matches the origin only by accident of the host it
 * names. And the serialised result is refused when IT starts with "//", because
 * dot-segment collapse turns "/.//evil.test" into the pathname "//evil.test" —
 * same-origin under the dummy base, protocol-relative the moment a caller
 * assigns it to `location.href`.
 */
export function validateRedirect(url: unknown): string | null {
  if (typeof url !== "string" || url === "") return null;
  if (!url.startsWith("/") || url.startsWith("//")) return null;

  let resolved: URL;
  try {
    resolved = new URL(url, GUARD_ORIGIN);
  } catch {
    return null;
  }
  if (resolved.origin !== GUARD_ORIGIN) return null;

  const target = `${resolved.pathname}${resolved.search}${resolved.hash}`;
  return target.startsWith("//") ? null : target;
}
